import { map, mergeMap, catchError, tap, filter } from 'rxjs/operators'
import { of } from 'rxjs'
import { ofType, combineEpics } from 'redux-observable'

import { logger } from '../../util/log.helpers'
import {
  EXCHANGE_PARTNER_ACTION,
  EXCHANGE_SETTINGS_ACTION,
  TOKEN_ACTIONS
} from '../../constants/actions'
import apiService from '../../services/api'
import { wrapUserAccessToken } from '../../util/auth.helpers'
import { commonParser } from '../../util/apiParser.helpers'
import {
  buildConnectExchangePartnerBody,
  buildPartnersQueryParams,
  buildWhitelistTokenTypesExchange,
  formatPartners,
  isExchangeFilterRefreshData
} from '../../util/exchange.helpers'
import {
  extractBrandId,
  extractMainProfile,
  extractTokenId
} from '../../util/epics.helpers'
import { trim } from '../../util/string.helpers'
import {
  extractTokenIds,
  extractBrandTokensFromMainData
} from '../../util/token.helpers'

const handleSaveExchangeSettingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(EXCHANGE_SETTINGS_ACTION.ON_SAVE_SETTINGS),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .saveExchangeSettings(
            extractSaveExchangeSettingsData(state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: EXCHANGE_SETTINGS_ACTION.ON_SAVE_SETTINGS_SUCCESS,
              payload: commonParser(response.data)
            })),
            catchError((error) =>
              of({
                type: EXCHANGE_SETTINGS_ACTION.ON_SAVE_SETTINGS_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleOnChangePartnersFiltersEpic = (action$) =>
  action$.pipe(
    ofType(EXCHANGE_PARTNER_ACTION.CHANGE_STATE),
    filter(({ payload: { property } }) =>
      isExchangeFilterRefreshData(property)
    ),
    mergeMap(() => [{ type: EXCHANGE_PARTNER_ACTION.ON_GET_PARTNERS }])
  )

const handleGetPartnersByFilterEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      EXCHANGE_PARTNER_ACTION.ON_GET_PARTNERS,
      EXCHANGE_PARTNER_ACTION.ON_CONNECT_EXCHANGE_SUCCESS,
      EXCHANGE_PARTNER_ACTION.ON_UPDATE_EXCHANGE_CONNECTION_SUCCESS,
      TOKEN_ACTIONS.ON_CREATE_TOKEN_SUCCESS
    ),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getPotentialPartners(buildPartnersQueryParams(state$), accessToken)
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: EXCHANGE_PARTNER_ACTION.ON_GET_PARTNERS_SUCCESS,
              payload: {
                exchangePartners: formatPartners(response.data),
                currentTokenId: extractTokenId(state$)
              }
            })),
            catchError((error) =>
              of({
                type: EXCHANGE_PARTNER_ACTION.ON_GET_PARTNERS_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleGetWhitelistByFilterEpic = (action$, state$) =>
  action$.pipe(
    ofType(EXCHANGE_PARTNER_ACTION.ON_GET_PARTNERS_SUCCESS),
    map(() => ({
      brandId: extractBrandId(state$),
      tokenId: extractTokenId(state$)
    })),
    filter(({ brandId, tokenId }) => brandId && tokenId),
    mergeMap(({ brandId }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getExchangeWhitelist(
            brandId,
            buildPartnersQueryParams(state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: EXCHANGE_PARTNER_ACTION.ON_GET_WHITELIST_SUCCESS,
              payload: {
                exchangePartners: formatPartners(response.data),
                ownTokenIds: extractTokenIds(
                  extractBrandTokensFromMainData(
                    extractMainProfile(state$),
                    brandId
                  )
                )
              }
            })),
            catchError((error) =>
              of({
                type: EXCHANGE_PARTNER_ACTION.ON_GET_WHITELIST_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleGetAllPartnersEpic = (action$, state$) =>
  action$.pipe(
    ofType(EXCHANGE_PARTNER_ACTION.ON_UPDATE_EXCHANGE_CONNECTION_SUCCESS),
    map(() => ({
      brandId: extractBrandId(state$),
      tokenId: extractTokenId(state$)
    })),
    filter(({ brandId, tokenId }) => brandId && tokenId),
    mergeMap(({ brandId, tokenId }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getExchangeWhitelist(
            brandId,
            buildWhitelistTokenTypesExchange({
              tokenId
            }),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: EXCHANGE_PARTNER_ACTION.ON_GET_ALL_PARTNERS_SUCCESS,
              payload: {
                exchangePartners: formatPartners(response.data),
                ownTokenIds: extractTokenIds(
                  extractBrandTokensFromMainData(
                    extractMainProfile(state$),
                    brandId
                  )
                )
              }
            })),
            catchError((error) =>
              of({
                type: EXCHANGE_PARTNER_ACTION.ON_GET_ALL_PARTNERS_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleConnectExchangePartnerEpic = (action$, state$) =>
  action$.pipe(
    ofType(EXCHANGE_PARTNER_ACTION.ON_CONNECT_EXCHANGE),
    mergeMap(({ payload: { toTokenId } }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .createExchangeConnection(
            extractBrandId(state$),
            buildConnectExchangePartnerBody(extractTokenId(state$), toTokenId),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: EXCHANGE_PARTNER_ACTION.ON_CONNECT_EXCHANGE_SUCCESS,
              payload: commonParser(response.data)
            })),
            catchError((error) =>
              of({
                type: EXCHANGE_PARTNER_ACTION.ON_CONNECT_EXCHANGE_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleUpdateExchangeConnectionEpic = (action$, state$) =>
  action$.pipe(
    ofType(EXCHANGE_PARTNER_ACTION.ON_UPDATE_EXCHANGE_CONNECTION),
    mergeMap(({ payload: { action, data, connection } }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .updateExchangeConnection(
            extractBrandId(state$),
            connection?.tokenExchangeWhitelist?.id,
            data,
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: EXCHANGE_PARTNER_ACTION.ON_UPDATE_EXCHANGE_CONNECTION_SUCCESS,
              payload: { action, connection, data: commonParser(response.data) }
            })),
            catchError((error) =>
              of({
                type: EXCHANGE_PARTNER_ACTION.ON_UPDATE_EXCHANGE_CONNECTION_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

export const exchangeEpic = combineEpics(
  handleSaveExchangeSettingsEpic,
  handleOnChangePartnersFiltersEpic,
  handleGetPartnersByFilterEpic,
  handleGetWhitelistByFilterEpic,
  handleGetAllPartnersEpic,
  handleConnectExchangePartnerEpic,
  handleUpdateExchangeConnectionEpic
)

const extractSaveExchangeSettingsData = (state) => {
  const exchangeReducer = state.value.exchangeReducer
  const {
    exchangeValueTemp,
    programUrlTemp,
    catalogueUrlTemp,
    loginIdTypeTemp,
    brandRequirementsTemp,
    additionalServicesTemp
  } = exchangeReducer.exchangeSettingsProfile
  return {
    exchange_value: exchangeValueTemp,
    program_url: trim(programUrlTemp),
    catalogue_url: trim(catalogueUrlTemp),
    login_id_type: loginIdTypeTemp,
    brand_requirements: trim(brandRequirementsTemp),
    additional_services: trim(additionalServicesTemp)
  }
}
